/**
 * Inventory API — products, units, TIM-E parts, BIM-E parts, operations (stored in DB).
 *
 * Every save here is a PUT-all (whole-array replace) against an endpoint that
 * deletes and re-inserts under a transaction. The server refuses obviously
 * destructive replaces (wiping a non-empty table with `[]`, or large shrinkage)
 * unless the caller opts in with `confirmReplaceAll: true`. Pass that flag only
 * from explicit "clear everything" UX, never from add/edit/delete flows.
 *
 * Unit saves additionally carry a `revision` so the server can tell a current list
 * from a stale one; see SaveInventoryUnitsOptions.
 *
 * Writes identify the caller (session token plus a fallback user id) so the server
 * can attribute each change in the inventory log.
 */

import { API_BASE } from '../config/apiBase';
import { actorJsonHeaders, withActor } from './session';

export type SaveInventoryOptions = {
  /** Required to allow the server to perform a destructive replace (e.g. emptying a non-empty table). */
  confirmReplaceAll?: boolean;
};

export type SaveInventoryUnitsOptions = SaveInventoryOptions & {
  /**
   * Fingerprint of the units list this save was built from, as returned by the
   * previous fetch or save. The server refuses the save if rows have been added or
   * removed since, so a tab left open for hours can no longer write its old list
   * back over everyone else's changes.
   */
  revision?: string | null;
};

export type InventoryUnitsSnapshot = {
  units: InventoryUnitRow[];
  revision: string | null;
};

/** Error thrown when a save fails; includes HTTP status and optional server-supplied code. */
export class InventorySaveError extends Error {
  status: number;
  code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'InventorySaveError';
    this.status = status;
    this.code = code;
  }
}

async function parseInventoryError(res: Response, fallback: string): Promise<InventorySaveError> {
  let parsed: { error?: string; code?: string } = {};
  try {
    parsed = await res.json();
  } catch {
    /* ignore non-JSON bodies */
  }
  return new InventorySaveError(parsed.error || `${fallback} (${res.status}).`, res.status, parsed.code);
}

export interface InventoryProductRow {
  id: string;
  name: string;
  type: string;
  sku: string;
  availability: number;
  inUse: number;
  lowStockBuffer?: number | null;
  catalogStatus?: 'Active' | 'Void';
  /** Display string e.g. "$120" — used to fill TIM-E monthly report when part row has no cost. */
  replacementCostPerItem?: string;
}

export interface InventoryUnitRow {
  id: string;
  productId: string;
  countryOfOrigin: string;
  productType?: string;
  model: string;
  serialNumber: string;
  /** Original temporary serial (TMP-…) carried over from an incoming shipment. */
  tempSerialNumber?: string;
  status?: string;
  condition?: string;
  manufacturer?: string;
  location?: string;
  business?: string;
  installationDate?: string;
  trashBins?: number;
  linenBins?: number;
  rollingBases?: number;
  staticBases?: number;
  stockQuantity?: number;
  assembled?: boolean;
}

export interface TimEPartsRow {
  id?: number;
  product: string;
  available: string;
  fullyAssembled?: string;
  notAssembled?: string;
  backOrdered?: string;
  pendingSales?: string;
  replacementCostPerItem?: string;
  value?: string;
  discardedUnits?: string;
  wasteDollars?: string;
  lowStockBuffer?: number | null;
  status?: 'Active' | 'Void';
}

export interface BimEPartsRow {
  id?: number;
  product: string;
  available: string;
  fullyAssembled?: string;
  notAssembled?: string;
  backOrdered?: string;
  pendingSales?: string;
  replacementCostPerItem?: string;
  value?: string;
  discardedUnits?: string;
  pendingDelivery?: string;
  lowStockBuffer?: number | null;
  status?: 'Active' | 'Void';
}

export interface OperationsRow {
  id?: number;
  product: string;
  available: string;
  replacementCostPerItem: string;
  value: string;
}

export async function fetchInventoryProducts(): Promise<InventoryProductRow[]> {
  const res = await fetch(`${API_BASE}/api/inventory/products`);
  if (!res.ok) throw new Error('Failed to fetch inventory products');
  const data = await res.json();
  return data.products ?? [];
}

export async function saveInventoryProducts(
  products: InventoryProductRow[],
  opts?: SaveInventoryOptions
): Promise<InventoryProductRow[]> {
  const res = await fetch(`${API_BASE}/api/inventory/products`, {
    method: 'PUT',
    headers: actorJsonHeaders(),
    body: JSON.stringify(withActor({ products, confirmReplaceAll: opts?.confirmReplaceAll === true })),
  });
  if (!res.ok) throw await parseInventoryError(res, 'Failed to save inventory products');
  const data = await res.json();
  return data.products ?? [];
}

/**
 * File write-in products (currently from the contract generator) into the catalog.
 * Additive: a product with the same name is reused instead of duplicated.
 */
export async function ensureInventoryProducts(
  products: Array<{ name: string; type?: string; replacementCostPerItem?: string }>
): Promise<{ created: Array<{ id: string; name: string }>; reused: Array<{ id: string; name: string }> }> {
  const res = await fetch(`${API_BASE}/api/inventory/products/ensure`, {
    method: 'POST',
    headers: actorJsonHeaders(),
    body: JSON.stringify(withActor({ products })),
  });
  if (!res.ok) throw await parseInventoryError(res, 'Failed to add products to the inventory catalog');
  const data = await res.json();
  return { created: data.created ?? [], reused: data.reused ?? [] };
}

/** Units plus the revision to send back with the next save. */
export async function fetchInventoryUnitsSnapshot(): Promise<InventoryUnitsSnapshot> {
  const res = await fetch(`${API_BASE}/api/inventory/units`);
  if (!res.ok) throw new Error('Failed to fetch inventory units');
  const data = await res.json();
  return { units: data.units ?? [], revision: typeof data.revision === 'string' ? data.revision : null };
}

/** Read-only callers that never save back and so don't need to track the revision. */
export async function fetchInventoryUnits(): Promise<InventoryUnitRow[]> {
  const { units } = await fetchInventoryUnitsSnapshot();
  return units;
}

export async function saveInventoryUnits(
  units: InventoryUnitRow[],
  opts?: SaveInventoryUnitsOptions
): Promise<InventoryUnitsSnapshot> {
  const res = await fetch(`${API_BASE}/api/inventory/units`, {
    method: 'PUT',
    headers: actorJsonHeaders(),
    body: JSON.stringify(
      withActor({
        units,
        revision: opts?.revision ?? undefined,
        confirmReplaceAll: opts?.confirmReplaceAll === true,
      })
    ),
  });
  if (!res.ok) throw await parseInventoryError(res, 'Failed to save inventory units');
  const data = await res.json();
  return { units: data.units ?? [], revision: typeof data.revision === 'string' ? data.revision : null };
}

/** Update a single inventory unit's fields (used by the shipment Arrived flow). */
export async function updateInventoryUnit(
  id: string,
  fields: Partial<InventoryUnitRow>
): Promise<InventoryUnitRow> {
  const res = await fetch(`${API_BASE}/api/inventory/units/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: actorJsonHeaders(),
    body: JSON.stringify(withActor(fields)),
  });
  if (!res.ok) throw await parseInventoryError(res, 'Failed to update inventory unit');
  const data = await res.json();
  return data.unit;
}

export async function fetchTimEParts(): Promise<TimEPartsRow[]> {
  const res = await fetch(`${API_BASE}/api/inventory/tim-e-parts`);
  if (!res.ok) throw new Error('Failed to fetch TIM-E parts');
  const data = await res.json();
  return data.rows ?? [];
}

export async function saveTimEParts(
  rows: TimEPartsRow[],
  opts?: SaveInventoryOptions
): Promise<TimEPartsRow[]> {
  const res = await fetch(`${API_BASE}/api/inventory/tim-e-parts`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ rows, confirmReplaceAll: opts?.confirmReplaceAll === true }),
  });
  if (!res.ok) throw await parseInventoryError(res, 'Failed to save TIM-E parts');
  const data = await res.json();
  return data.rows ?? [];
}

export async function fetchBimEParts(): Promise<BimEPartsRow[]> {
  const res = await fetch(`${API_BASE}/api/inventory/bim-e-parts`);
  if (!res.ok) throw new Error('Failed to fetch BIM-E parts');
  const data = await res.json();
  return data.rows ?? [];
}

export async function saveBimEParts(
  rows: BimEPartsRow[],
  opts?: SaveInventoryOptions
): Promise<BimEPartsRow[]> {
  const res = await fetch(`${API_BASE}/api/inventory/bim-e-parts`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ rows, confirmReplaceAll: opts?.confirmReplaceAll === true }),
  });
  if (!res.ok) throw await parseInventoryError(res, 'Failed to save BIM-E parts');
  const data = await res.json();
  return data.rows ?? [];
}

export async function fetchOperationsInventory(): Promise<OperationsRow[]> {
  const res = await fetch(`${API_BASE}/api/inventory/operations`);
  if (!res.ok) throw new Error('Failed to fetch operations inventory');
  const data = await res.json();
  return data.rows ?? [];
}

export async function saveOperationsInventory(
  rows: OperationsRow[],
  opts?: SaveInventoryOptions
): Promise<OperationsRow[]> {
  const res = await fetch(`${API_BASE}/api/inventory/operations`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ rows, confirmReplaceAll: opts?.confirmReplaceAll === true }),
  });
  if (!res.ok) throw await parseInventoryError(res, 'Failed to save operations inventory');
  const data = await res.json();
  return data.rows ?? [];
}
